if (!customElements.get('td-collection-load-more')) {
  class TdCollectionLoadMore extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        grid: '[data-td-product-grid]',
        label: '[data-td-load-more-label]',
        link: '[data-td-load-more-link]',
        root: 'td-collection-load-more[data-td-load-more-root]',
        scrollTrigger: '.scroll-trigger',
        status: '[data-td-load-more-status]',
      };
      this.handleClick = this.handleClick.bind(this);
      this.abortController = null;
      this.isLoading = false;
    }

    connectedCallback() {
      const sectionEl = document.getElementById(`shopify-section-${this.dataset.tdSectionId}`);
      this.grid = sectionEl
        ? sectionEl.querySelector(this.selectors.grid)
        : null;
      this.link = this.querySelector(this.selectors.link);
      this.label = this.querySelector(this.selectors.label);
      this.status = this.querySelector(this.selectors.status);

      if (!this.grid || !this.link) return;

      this.link.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      if (this.link) {
        this.link.removeEventListener('click', this.handleClick);
      }

      if (this.abortController) {
        this.abortController.abort();
      }
    }

    handleClick(event) {
      if (!this.dataset.tdNextUrl || this.isLoading) return;

      event.preventDefault();
      this.loadNextPage();
    }

    setStatus(message) {
      if (this.status) {
        this.status.textContent = message || '';
      }
    }

    setLoadingState(isLoading) {
      this.isLoading = isLoading;
      if (isLoading) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }

      if (this.grid) {
        this.grid.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      if (this.link) {
        if (isLoading) {
          this.link.setAttribute('aria-disabled', 'true');
          this.link.tabIndex = -1;
        } else {
          this.link.removeAttribute('aria-disabled');
          this.link.removeAttribute('tabindex');
        }
      }

      if (this.label) {
        this.label.textContent = isLoading ? this.dataset.tdLoadingText : this.dataset.tdButtonText;
      }
    }

    buildRequestUrl() {
      const url = new URL(this.dataset.tdNextUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.tdSectionId);
      return url;
    }

    async loadNextPage() {
      this.setStatus(this.dataset.tdLoadingText);
      this.setLoadingState(true);

      try {
        this.abortController = new AbortController();
        const response = await fetch(this.buildRequestUrl().toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch next collection page: ${response.status}`);
        }

        const html = await response.text();
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = parsedHTML.querySelector(this.selectors.grid);
        const nextItems = nextGrid ? Array.from(nextGrid.children) : [];

        if (!nextGrid || nextItems.length === 0) {
          throw new Error('Fetched collection page did not include any products.');
        }

        const fragment = document.createDocumentFragment();
        nextItems.forEach((item) => {
          item.querySelectorAll(this.selectors.scrollTrigger).forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
          });
          fragment.appendChild(item);
        });
        this.grid.appendChild(fragment);

        const replacement = parsedHTML.querySelector(this.selectors.root);

        if (replacement && replacement.dataset.tdNextUrl) {
          const replacementLink = replacement.querySelector(this.selectors.link);
          this.dataset.tdNextUrl = replacement.dataset.tdNextUrl;

          if (replacementLink) {
            this.link.href = replacementLink.href;
          }
        } else {
          this.removeAttribute('data-td-next-url');

          if (this.link) {
            this.link.remove();
            this.link = null;
          }
        }

        this.setStatus(this.dataset.tdLoadedText);
      } catch (error) {
        if (error.name === 'AbortError') return;

        this.setStatus(this.dataset.tdErrorText);
      } finally {
        this.abortController = null;
        this.setLoadingState(false);
      }
    }
  }

  customElements.define('td-collection-load-more', TdCollectionLoadMore);
}
